import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AttachBookDialog } from "@/components/AttachBookDialog";
import { useAuth } from "@/context/AuthContext";
import { Library, Plus, BookOpen, Loader2, ArrowRight, Sparkles, Users, GitBranch, Wand2, ChevronDown, Globe, X } from "lucide-react";

export function SagaPanel({ projects, navigate, onContinueSaga, onChanged }) {
  const aiOn = useAuth()?.user?.ai_tools_enabled !== false;
  const [removeTarget, setRemoveTarget] = useState(null); // { saga, book }
  const [attachFor, setAttachFor] = useState(null);
  const [sagas, setSagas] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [firstBook, setFirstBook] = useState("");
  const [busy, setBusy] = useState(false);
  const [addFor, setAddFor] = useState(null); // saga object
  const [bk, setBk] = useState({ title: "", logline: "", premise: "" });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [storySoFar, setStorySoFar] = useState("");
  const [showRecap, setShowRecap] = useState(false);
  const [outline, setOutline] = useState(null); // { synopsis, acts:[{title,pitch}] }
  const [outlineLoading, setOutlineLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/series").then(({ data }) => setSagas(data)).catch(() => setSagas([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const standalone = (projects || []).filter((p) => !p.series_id);

  const createSaga = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/series", { title: title.trim(), first_project_id: firstBook || null });
      toast.success("Saga created");
      setNewOpen(false); setTitle(""); setFirstBook("");
      load();
    } catch { toast.error("Couldn't create the saga"); }
    finally { setBusy(false); }
  };

  const removeBook = async () => {
    const { saga, book } = removeTarget;
    setBusy(true);
    try {
      const { data } = await api.post(`/series/${saga.id}/books/${book.id}/remove`);
      toast.success(data.series_deleted ? `Removed "${book.title}" — the saga was empty, so it was deleted`
        : `Removed "${book.title}" from ${saga.title}`);
      setRemoveTarget(null);
      load();
      onChanged?.();
    } catch { toast.error("Couldn't remove the book from the saga"); }
    finally { setBusy(false); }
  };

  const openAddBook = (s) => {
    setAddFor(s);
    setBk({ title: `Book ${s.books.length + 1}`, logline: "", premise: "" });
    setPreview(null); setStorySoFar(""); setShowRecap(false); setOutline(null);
    setPreviewLoading(true);
    api.get(`/series/${s.id}/continuation-preview`)
      .then(({ data }) => { setPreview(data); setStorySoFar(data.story_so_far || ""); })
      .catch(() => setPreview({ carried_characters: [], open_threads: [], character_states: [], world_changes: [] }))
      .finally(() => setPreviewLoading(false));
  };

  const draftOutline = async () => {
    if (!addFor) return;
    setOutlineLoading(true);
    try {
      const { data } = await api.post(`/series/${addFor.id}/continuation-outline`, {
        logline: bk.logline.trim(), premise: bk.premise.trim(),
      });
      setOutline(data);
    } catch { toast.error("Couldn't draft the outline — try again"); }
    finally { setOutlineLoading(false); }
  };

  const updateAct = (i, patch) => setOutline((o) => ({ ...o, acts: o.acts.map((a, j) => j === i ? { ...a, ...patch } : a) }));

  const closeAddBook = () => {
    setAddFor(null); setPreview(null); setOutline(null); setStorySoFar(""); setShowRecap(false);
    setBk({ title: "", logline: "", premise: "" });
  };

  const addBook = async () => {
    if (!addFor) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/series/${addFor.id}/books`, {
        title: bk.title.trim(), logline: bk.logline.trim(), premise: bk.premise.trim(),
        story_so_far: storySoFar.trim(),
        synopsis: outline?.synopsis?.trim() || "",
        acts: outline?.acts || [],
      });
      toast.success(`Book ${data.series_order} created — carried ${data.carried_entities} canon detail${data.carried_entities === 1 ? "" : "s"} + a "story so far" recap forward`);
      closeAddBook();
      navigate(`/project/${data.project_id}`);
    } catch { toast.error("Couldn't add the next book"); }
    finally { setBusy(false); }
  };

  const ORD = ["", "①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];

  return (
    <section className="mb-12 fade-up" data-testid="saga-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary/12 flex items-center justify-center">
            <Library className="w-4.5 h-4.5 text-primary" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold leading-none">Sagas &amp; series</h2>
            <p className="text-xs text-muted-foreground mt-1">Group books in order — your Codex canon and a “story so far” recap carry forward automatically.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setNewOpen(true)} data-testid="new-saga-button">
          <Plus className="w-4 h-4" /> New saga
        </Button>
      </div>

      {sagas === null ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : sagas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 px-6 py-6 text-sm text-muted-foreground" data-testid="saga-empty">
          No sagas yet. Writing a trilogy or a series? Create a saga to keep continuity across books.
        </div>
      ) : (
        <div className="space-y-4">
          {sagas.map((s) => (
            <div key={s.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`saga-card-${s.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-primary" />
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <span className="text-xs text-muted-foreground">· {s.books.length} book{s.books.length === 1 ? "" : "s"}</span>
              </div>
              <div className="flex flex-wrap items-stretch gap-2.5">
                {s.books.map((b) => (
                  <div key={b.id} className="group relative">
                    <button onClick={() => navigate(`/project/${b.id}`)} data-testid={`saga-book-${b.id}`}
                      className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 hover:border-primary/50 hover:bg-secondary transition-all px-3.5 py-2.5 pr-9 text-left">
                      <span className="text-primary font-semibold text-base leading-none">{ORD[b.order] || b.order}</span>
                      <span className="text-sm font-medium max-w-[12rem] truncate group-hover:text-primary transition-colors">{b.title}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </button>
                    <button onClick={() => setRemoveTarget({ saga: s, book: b })} title="Remove from saga"
                      data-testid={`saga-remove-book-${b.id}`}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground opacity-60 hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-[opacity,color,background-color]">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button onClick={() => onContinueSaga ? onContinueSaga(s) : openAddBook(s)}
                  data-testid={`add-next-book-${s.id}`}
                  className="flex items-center gap-1.5 rounded-xl border border-dashed border-primary/40 text-primary hover:bg-primary/5 transition-colors px-3.5 py-2.5 text-sm font-medium">
                  <Plus className="w-4 h-4" /> Add the next book
                </button>
                <button onClick={() => setAttachFor(s)} data-testid={`attach-existing-book-${s.id}`}
                  className="flex items-center gap-1.5 rounded-xl border border-dashed border-border text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors px-3.5 py-2.5 text-sm font-medium">
                  <BookOpen className="w-4 h-4" /> Add existing book
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AttachBookDialog saga={attachFor} books={standalone} onClose={() => setAttachFor(null)}
        onDone={() => { setAttachFor(null); load(); onChanged?.(); }} />

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="remove-saga-book-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{removeTarget?.book.title}" from {removeTarget?.saga.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              The book itself isn't deleted or changed — chapters, Codex, cover and its story-so-far recap all stay.
              The remaining books will be renumbered.
              {removeTarget?.saga.books.length === 1 && " This is the only book, so the empty saga will be deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-saga-book-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); removeBook(); }} disabled={busy}
              data-testid="remove-saga-book-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove from saga"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New saga */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-popover" data-testid="new-saga-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Library className="w-5 h-5 text-primary" /> Create a saga</DialogTitle>
            <DialogDescription>Give your series a name. You can start it with an existing book, or add books later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Saga title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
                placeholder="The Ember Chronicles" data-testid="saga-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Start with an existing book as Book 1 <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <select value={firstBook} onChange={(e) => setFirstBook(e.target.value)} data-testid="saga-first-book-select"
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">— None (empty saga) —</option>
                {standalone.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createSaga} disabled={busy || !title.trim()} data-testid="create-saga-submit" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Library className="w-4 h-4" />} Create saga
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add next book */}
      <Dialog open={!!addFor} onOpenChange={(v) => !v && closeAddBook()}>
        <DialogContent className="bg-popover max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Add the next book
              {preview?.book_number ? <span className="text-sm font-normal text-muted-foreground">· Book {preview.book_number} of “{addFor?.title}”</span> : null}
            </DialogTitle>
            <DialogDescription>
              Your world (Codex), voice, pacing, humour and rivalry banter all carry over. I read the previous book so this one genuinely continues — just tell me what happens next.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Book title</Label>
              <Input value={bk.title} onChange={(e) => setBk({ ...bk, title: e.target.value })} autoFocus
                placeholder={`Book ${(addFor?.books.length || 0) + 1}`} data-testid="add-book-title" />
            </div>
            <div className="space-y-1.5">
              <Label>What happens in this book? <span className="text-muted-foreground font-normal">(one line)</span></Label>
              <Input value={bk.logline} onChange={(e) => setBk({ ...bk, logline: e.target.value })}
                placeholder="e.g. Two years on, Smithy is dragged back into the bracket…" data-testid="add-book-logline" />
            </div>
            <div className="space-y-1.5">
              <Label>Where it picks up / the plot you want <span className="text-muted-foreground font-normal">(prose, optional)</span></Label>
              <Textarea value={bk.premise} onChange={(e) => setBk({ ...bk, premise: e.target.value })} rows={3}
                placeholder="A quick prose of what's happening in this next book…" data-testid="add-book-premise" />
            </div>

            {/* Continuation preview — what's carried from the previous book */}
            {previewLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-xl border border-border bg-secondary/30 px-4 py-3" data-testid="add-book-preview-loading">
                <Loader2 className="w-4 h-4 animate-spin text-primary" /> Reading “{addFor?.title}” to set up the continuation…
              </div>
            ) : preview && preview.book_number > 1 ? (
              <div className="space-y-3 rounded-xl border border-border bg-secondary/25 p-4" data-testid="add-book-preview">
                {preview.carried_characters?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2"><Users className="w-3.5 h-3.5 text-primary" /> Returning cast ({preview.carried_count} Codex entries carried)</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="add-book-cast">
                      {preview.carried_characters.map((c, i) => (
                        <span key={i} className="text-[11px] rounded-full bg-primary/10 text-primary px-2.5 py-1 font-medium">{c.name}</span>
                      ))}
                    </div>
                  </div>
                )}
                {preview.open_threads?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2"><GitBranch className="w-3.5 h-3.5 text-primary" /> Open threads from the last book</div>
                    <ul className="space-y-1" data-testid="add-book-threads">
                      {preview.open_threads.map((t, i) => (
                        <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-primary">•</span><span>{t}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {preview.world_changes?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2"><Globe className="w-3.5 h-3.5 text-primary" /> Where the world stands</div>
                    <ul className="space-y-1" data-testid="add-book-world">
                      {preview.world_changes.map((t, i) => (
                        <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-primary">•</span><span>{t}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* editable story so far */}
                <div>
                  <button type="button" onClick={() => setShowRecap((v) => !v)} data-testid="add-book-recap-toggle"
                    className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRecap ? "rotate-180" : ""}`} /> Story so far (editable canon)
                  </button>
                  {showRecap && (
                    <Textarea value={storySoFar} onChange={(e) => setStorySoFar(e.target.value)} rows={5}
                      className="mt-2 text-xs" data-testid="add-book-recap"
                      placeholder="The recap of the previous book the AI will honour as canon…" />
                  )}
                </div>
              </div>
            ) : null}

            {/* Bridging outline */}
            {aiOn && preview && preview.book_number > 1 && (
              <div className="space-y-2">
                <Button type="button" variant="outline" size="sm" onClick={draftOutline} disabled={outlineLoading}
                  className="gap-2" data-testid="add-book-draft-outline">
                  {outlineLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 text-primary" />}
                  {outline ? "Redraft continuation outline" : "Draft a continuation outline"}
                </Button>
                {outline && (
                  <div className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="add-book-outline">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Synopsis</Label>
                      <Textarea value={outline.synopsis} onChange={(e) => setOutline({ ...outline, synopsis: e.target.value })}
                        rows={3} className="text-xs" data-testid="add-book-synopsis" />
                    </div>
                    {outline.acts?.map((a, i) => (
                      <div key={i} className="space-y-1.5">
                        <Input value={a.title} onChange={(e) => updateAct(i, { title: e.target.value })}
                          className="h-8 text-sm font-semibold" data-testid={`add-book-act-title-${i}`} />
                        <Textarea value={a.pitch} onChange={(e) => updateAct(i, { pitch: e.target.value })}
                          rows={2} className="text-xs" data-testid={`add-book-act-pitch-${i}`} />
                      </div>
                    ))}
                    <p className="text-[11px] text-muted-foreground">This outline scaffolds the new book's acts. You can edit it here or later inside the project.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeAddBook}>Cancel</Button>
            <Button onClick={addBook} disabled={busy || previewLoading} data-testid="add-book-submit" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default SagaPanel;
